import { useTopOffer } from 'webReservations/hooks/useTopOffer';
import { Button, ButtonVariants } from '@components/button/Button';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { centsToWholeDollar } from '@utils/currency';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { AcceptTopOfferModal } from '../offers/AcceptTopOfferModal';
import { sendGa4OfferAcceptStartEvent } from '../offers/gaHelpers';
import { type Reservation } from './apiHelpers';
import styles from './ReservationCardActions.scss';
import { UpdateListPriceModal } from './UpdateListPriceModal';

export interface ReservationCardActionsProps {
  buyNowPrice: number | null;
  fetchConfirmedReservations: () => Promise<void>;
  reservation: Reservation;
}

export const ReservationCardActions = ({
  buyNowPrice,
  fetchConfirmedReservations,
  reservation,
}: ReservationCardActionsProps) => {
  const {
    close: closeAcceptTopOfferModal,
    isOpen: isAcceptTopOfferModalOpen,
    open: openAcceptTopOfferModal,
  } = useIsOpen();
  const {
    close: closeUpdateListPriceModal,
    isOpen: isUpdateListPriceModalOpen,
    open: openUpdateListPriceModal,
  } = useIsOpen();

  const { data: topOffer } = useTopOffer(reservation);

  const hasOffer = !!topOffer?.id;
  const hasActions = buyNowPrice || hasOffer;

  return (
    <div className={styles.container} data-testid="card-actions">
      {hasActions ? (
        <>
          <div className={styles.sellNowContainer}>
            {hasOffer ? (
              <>
                <Button
                  className={typography.h8}
                  label={`Sell now for ${centsToWholeDollar(topOffer.price)}`}
                  onClick={() => {
                    sendGa4OfferAcceptStartEvent({
                      date: reservation.date,
                      offerAmount: topOffer.price,
                      publicName: reservation.listing?.publicName || 'N/A',
                      restaurantName: reservation.restaurant.name,
                      time: reservation.time,
                    });
                    openAcceptTopOfferModal();
                  }}
                  variant={ButtonVariants.Primary}
                />
                <AcceptTopOfferModal
                  closeModal={closeAcceptTopOfferModal}
                  isOpen={isAcceptTopOfferModalOpen}
                  offer={topOffer}
                  reservation={reservation}
                />
              </>
            ) : null}
          </div>
          {buyNowPrice && (
            <>
              <Button
                className={cx(typography.h7, styles.listPriceButton)}
                label={`List Price ${centsToWholeDollar(buyNowPrice)}`}
                onClick={openUpdateListPriceModal}
                variant={ButtonVariants.Default}
              />
              <UpdateListPriceModal
                closeModal={closeUpdateListPriceModal}
                isOpen={isUpdateListPriceModalOpen}
                listPrice={buyNowPrice}
                onSubmit={fetchConfirmedReservations}
                reservationId={reservation.id}
              />
            </>
          )}
        </>
      ) : null}
    </div>
  );
};
